/**
 * Public Cache Service
 *
 * Provides public data access without SharePoint authentication.
 * Data is populated by admin panels and consumed by public pages.
 * No direct SharePoint access - uses localStorage as the cache layer.
 */

import { PublicKit } from '../types/kit'

// Public interfaces (no SharePoint-specific fields)
export interface PublicClass {
  id: string
  title: string
  description: string
  instructor: string
  date: string
  time: string
  duration: string
  price: number
  capacity: number
  enrolled: number
  level: 'Beginner' | 'Intermediate' | 'Advanced' | 'All Levels'
  imageId?: string
  isActive: boolean
  tags: string[]
  materials: string[]
  prerequisites?: string
}

export interface PublicGalleryItem {
  id: string
  title: string
  artist: string
  medium: string
  price?: number
  forSale: boolean
  description: string
  imageId?: string
  isActive: boolean
  category: string
  dateCreated: string
  isShopifyProduct?: boolean
  shopifyData?: {
    id: string
    handle: string
    updatedAt: string
    images: any[]
  }
}

export interface PublicImage {
  id: string
  filename?: string  // Optional for backward compatibility
  fileName?: string  // Support both naming conventions
  dataUrl: string
  category: string
  uploadDate?: string  // Optional for backward compatibility
  createdAt?: string   // Support both naming conventions
  lastUpdated?: string
  // Gallery-specific fields
  title?: string
  description?: string
  tags?: string[]
  isActive?: boolean
}

class PublicCacheService {
  private listeners: Map<string, Set<() => void>> = new Map()
  private storageListenerInitialized = false

  constructor() {
    this.initializeStorageListener()
  }

  /**
   * Initialize localStorage event listener to detect changes from other tabs/windows
   */
  private initializeStorageListener(): void {
    if (this.storageListenerInitialized || typeof window === 'undefined') {
      return
    }

    window.addEventListener('storage', (e) => {
      // Detect changes to our localStorage keys
      if (e.key && (
        e.key === 'salty_mermaid_classes' ||
        e.key === 'salty_mermaid_kits' ||
        e.key === 'salty_mermaid_gallery_items' ||
        e.key === 'salty_mermaid_public_images'
      )) {
        console.log('PublicCache: localStorage changed from another tab:', e.key)
        this.emit('cache_updated')
      }
    })

    this.storageListenerInitialized = true
  }

  /**
   * Subscribe to cache update events
   * @param event - Event name (e.g., 'cache_updated')
   * @param callback - Function to call when event fires
   * @returns Unsubscribe function
   */
  subscribe(event: string, callback: () => void): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event)!.add(callback)

    return () => {
      const eventListeners = this.listeners.get(event)
      if (eventListeners) {
        eventListeners.delete(callback)
      }
    }
  }

  /**
   * Emit an event to all subscribers
   * @param event - Event name to emit
   */
  private emit(event: string): void {
    const eventListeners = this.listeners.get(event)
    if (eventListeners) {
      eventListeners.forEach(callback => {
        try {
          callback()
        } catch (error) {
          console.error(`PublicCache: Error in ${event} listener:`, error)
        }
      })
    }
  }

  updateClassesCache(classes: PublicClass[]): void {
    // No-op - data is managed in JSON files
    console.log('PublicCache: updateClassesCache is no-op in JSON-only mode')
  }

  updateKitsCache(kits: PublicKit[]): void {
    // No-op - data is managed in JSON files
    console.log('PublicCache: updateKitsCache is no-op in JSON-only mode')
  }

  updateGalleryCache(items: PublicGalleryItem[]): void {
    // No-op - data is managed in JSON files
    console.log('PublicCache: updateGalleryCache is no-op in JSON-only mode')
  }

  updateImagesCache(images: PublicImage[]): void {
    // Store images in localStorage for public access
    try {
      localStorage.setItem('salty_mermaid_public_images', JSON.stringify(images))
      console.log('PublicCache: Updated images cache with', images.length, 'images')
      this.emit('cache_updated')
    } catch (error) {
      console.error('PublicCache: Error updating images cache:', error)
    }
  }

  /**
   * Manually notify subscribers that cache has been updated
   * Useful when external code updates localStorage directly
   */
  notifyCacheUpdated(): void {
    this.emit('cache_updated')
  }

  getLastUpdateTime(): Date | null {
    // No-op - JSON files don't track update times
    return null
  }

  isCacheEmpty(): boolean {
    // No-op - JSON files are always considered populated if they exist
    return false
  }

  clearCache(): void {
    // No-op - JSON files are the source of truth
    console.log('PublicCache: clearCache is no-op in JSON-only mode')
  }

  // Classes
  async getClasses(): Promise<PublicClass[]> {
    try {
      // Load from admin classes storage (single source of truth)
      const storedClasses = localStorage.getItem('salty_mermaid_classes')
      if (storedClasses) {
        const classes = JSON.parse(storedClasses)
        console.log('PublicCache: Loaded classes from localStorage:', classes.length)
        return classes
      }

      // Fallback to JSON file
      const response = await fetch('/data/classes.json')
      if (response.ok) {
        const classes = await response.json()
        console.log('PublicCache: Loaded classes from JSON file (fallback):', classes.length)
        return classes
      }
      console.log('PublicCache: Could not fetch classes.json')
      return []
    } catch (error) {
      console.error('PublicCache: Error loading classes:', error)
      return []
    }
  }

  async getActiveClasses(): Promise<PublicClass[]> {
    const classes = await this.getClasses()
    return classes.filter(c => c.isActive)
  }

  async getClass(id: string): Promise<PublicClass | null> {
    const classes = await this.getClasses()
    return classes.find(c => c.id === id) || null
  }

  // Kits
  async getKits(): Promise<PublicKit[]> {
    try {
      // Load from admin kits storage (single source of truth)
      const storedKits = localStorage.getItem('salty_mermaid_kits')
      if (storedKits) {
        const kits = JSON.parse(storedKits)
        console.log('PublicCache: Loaded kits from localStorage:', kits.length)
        // All kits now use stockCount (normalized in AdminProductsManager)
        // Temporary fallback for legacy data (remove after migration complete)
        return kits.map((kit: any) => ({
          ...kit,
          stockCount: kit.stockCount ?? kit.stockQuantity ?? 0
        }))
      }

      // Fallback to JSON file
      const response = await fetch('/data/kits.json')
      if (response.ok) {
        const kits = await response.json()
        console.log('PublicCache: Loaded kits from JSON file (fallback):', kits.length)
        // All kits now use stockCount (normalized in AdminProductsManager)
        // Temporary fallback for legacy data (remove after migration complete)
        return kits.map((kit: any) => ({
          ...kit,
          stockCount: kit.stockCount ?? kit.stockQuantity ?? 0
        }))
      }
      console.log('PublicCache: Could not fetch kits.json')
      return []
    } catch (error) {
      console.error('PublicCache: Error loading kits:', error)
      return []
    }
  }

  async getActiveKits(): Promise<PublicKit[]> {
    const kits = await this.getKits()
    // Filter for active kits only - don't filter by stock since admin controls what's active
    return kits.filter(k => k.isActive)
  }

  async getKit(id: string): Promise<PublicKit | null> {
    const kits = await this.getKits()
    return kits.find(k => k.id === id) || null
  }

  // Gallery Items
  async getGalleryItems(): Promise<PublicGalleryItem[]> {
    try {
      // Load from admin gallery storage (single source of truth)
      const storedGallery = localStorage.getItem('salty_mermaid_gallery_items')
      if (storedGallery) {
        const items = JSON.parse(storedGallery)
        console.log('PublicCache: Loaded gallery items from localStorage:', items.length)
        return items
      }

      // Fallback to JSON file
      const response = await fetch('/data/gallery.json')
      if (response.ok) {
        const items = await response.json()
        console.log('PublicCache: Loaded gallery items from JSON file (fallback):', items.length)
        return items
      }
      console.log('PublicCache: Could not fetch gallery.json')
      return []
    } catch (error) {
      console.error('PublicCache: Error loading gallery items:', error)
      return []
    }
  }

  async getActiveGalleryItems(): Promise<PublicGalleryItem[]> {
    const items = await this.getGalleryItems()
    return items.filter(g => g.isActive)
  }

  async getGalleryItem(id: string): Promise<PublicGalleryItem | null> {
    const items = await this.getGalleryItems()
    return items.find(g => g.id === id) || null
  }

  // Images
  async getImages(): Promise<PublicImage[]> {
    try {
      // First try to load from localStorage (updated by admin panels)
      const storedImages = localStorage.getItem('salty_mermaid_public_images')
      if (storedImages) {
        const images = JSON.parse(storedImages)
        console.log('PublicCache: Loaded images from localStorage:', images.length)
        return images
      }

      // Fallback to JSON file
      const response = await fetch('/data/images.json')
      if (response.ok) {
        const images = await response.json()
        console.log('PublicCache: Loaded images from JSON file (fallback):', images.length)
        return images
      }
      console.log('PublicCache: Could not fetch images.json')
      return []
    } catch (error) {
      console.error('PublicCache: Error loading images:', error)
      return []
    }
  }

  async getImagesByCategory(category: string): Promise<PublicImage[]> {
    const images = await this.getImages()
    return images.filter(img => img.category === category)
  }

  async getImage(id: string): Promise<PublicImage | null> {
    const images = await this.getImages()
    return images.find(img => img.id === id) || null
  }

  // Data info
  async isEmpty(): Promise<boolean> {
    const galleryItems = await this.getGalleryItems()
    const classes = await this.getClasses()
    const kits = await this.getKits()
    const images = await this.getImages()
    return (
      classes.length === 0 &&
      kits.length === 0 &&
      galleryItems.length === 0 &&
      images.length === 0
    )
  }

  // JSON-only system - all data stored directly in JSON files
  // Images are referenced by imageId and loaded from images.json
}

export const publicCacheService = new PublicCacheService()